import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Building2, CheckCircle2, Loader2, ShieldCheck, XCircle, Clock, KeyRound } from "lucide-react";

declare global {
  interface Window { Plaid: any; }
}

type PageState = "loading" | "invalid" | "expired" | "used" | "otp_pending" | "otp_input" | "ready" | "connecting" | "success" | "error";

export default function ConnectBankPage() {
  const [state, setState] = useState<PageState>("loading");
  const [businessName, setBusinessName] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    fetch(`/api/bank-connection/validate?token=${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(data => {
        if (data.valid) {
          setBusinessName(data.businessName);
          setState(data.emailVerified ? "ready" : "otp_pending");
        } else {
          if (data.reason === "expired") setState("expired");
          else if (data.reason === "used") setState("used");
          else setState("invalid");
        }
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const handleSendOtp = async () => {
    setOtpSending(true);
    try {
      const res = await fetch("/api/bank-connection/send-otp", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (data.success) setState("otp_input");
      else setErrorMsg(data.message || "Failed to send code");
    } catch { setErrorMsg("Network error"); }
    setOtpSending(false);
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) return;
    setOtpVerifying(true);
    try {
      const res = await fetch("/api/bank-connection/verify-otp", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, otp }),
      });
      const data = await res.json();
      if (data.verified) { setState("ready"); setErrorMsg(""); }
      else { setErrorMsg(data.message || "Invalid code"); }
    } catch { setErrorMsg("Verification failed"); }
    setOtpVerifying(false);
  };

  const handleConnect = async () => {
    setState("connecting");
    try {
      const res = await fetch("/api/bank-connection/create-link-token", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (!data.linkToken) throw new Error(data.message || "Failed to initialize");
      if (!window.Plaid) throw new Error("Plaid not loaded. Please refresh.");

      const handler = window.Plaid.create({
        token: data.linkToken,
        onSuccess: async (publicToken: string, metadata: any) => {
          try {
            const exRes = await fetch("/api/bank-connection/exchange", {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ token, publicToken, institutionName: metadata?.institution?.name || null }),
            });
            const exData = await exRes.json();
            if (exData.success) setState("success");
            else { setErrorMsg(exData.message || "Connection failed"); setState("error"); }
          } catch { setErrorMsg("Connection failed"); setState("error"); }
        },
        onExit: () => { if (state === "connecting") setState("ready"); },
      });
      handler.open();
    } catch (err: any) {
      setErrorMsg(err.message || "Something went wrong");
      setState("error");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md rounded-2xl shadow-lg border-gray-200">
        <CardContent className="p-8 text-center">

          {state === "loading" && <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto" />}

          {state === "invalid" && (
            <>
              <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Invalid Link</h2>
              <p className="text-sm text-gray-500">This bank connection link is not valid. Please request a new one from your accountant.</p>
            </>
          )}

          {state === "expired" && (
            <>
              <Clock className="h-12 w-12 text-amber-400 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Link Expired</h2>
              <p className="text-sm text-gray-500 mb-4">This link has expired. Please ask your accountant to send a new one.</p>
            </>
          )}

          {state === "used" && (
            <>
              <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Already Connected</h2>
              <p className="text-sm text-gray-500">This link has already been used to connect a bank account.</p>
            </>
          )}

          {state === "otp_pending" && (
            <>
              <div className="h-14 w-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-5">
                <KeyRound className="h-7 w-7 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Verify Your Identity</h2>
              <p className="text-sm text-gray-500 mb-6">
                We'll send a verification code to your email to confirm your identity before connecting a bank for <strong>{businessName}</strong>.
              </p>
              <Button onClick={handleSendOtp} disabled={otpSending} className="w-full h-11 rounded-xl gap-2">
                {otpSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                {otpSending ? "Sending..." : "Send Verification Code"}
              </Button>
            </>
          )}

          {state === "otp_input" && (
            <>
              <div className="h-14 w-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-5">
                <KeyRound className="h-7 w-7 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Enter Verification Code</h2>
              <p className="text-sm text-gray-500 mb-4">Check your email for a 6-digit code.</p>
              <Input
                value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000" maxLength={6}
                className="text-center text-2xl tracking-[0.5em] font-mono h-14 rounded-xl mb-4"
              />
              {errorMsg && <p className="text-sm text-red-500 mb-3">{errorMsg}</p>}
              <Button onClick={handleVerifyOtp} disabled={otp.length !== 6 || otpVerifying} className="w-full h-11 rounded-xl">
                {otpVerifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {otpVerifying ? "Verifying..." : "Verify"}
              </Button>
              <button onClick={handleSendOtp} disabled={otpSending} className="mt-3 text-xs text-blue-600 hover:underline">
                {otpSending ? "Sending..." : "Resend code"}
              </button>
            </>
          )}

          {state === "ready" && (
            <>
              <div className="h-14 w-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-5">
                <Building2 className="h-7 w-7 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Connect Bank Account</h2>
              <p className="text-sm text-gray-500 mb-6">
                Securely connect a bank account for <strong>{businessName}</strong>
              </p>
              <Button onClick={handleConnect} className="w-full h-11 rounded-xl gap-2">
                <ShieldCheck className="h-4 w-4" /> Connect Bank Securely
              </Button>
              <p className="text-[11px] text-gray-400 mt-4">Powered by Plaid. Your credentials are never shared.</p>
            </>
          )}

          {state === "connecting" && (
            <>
              <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-4" />
              <p className="text-sm text-gray-500">Connecting to your bank...</p>
            </>
          )}

          {state === "success" && (
            <>
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Bank Connected</h2>
              <p className="text-sm text-gray-500">Your bank account has been securely linked to <strong>{businessName}</strong>. You can close this page.</p>
            </>
          )}

          {state === "error" && (
            <>
              <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Connection Failed</h2>
              <p className="text-sm text-gray-500 mb-4">{errorMsg}</p>
              <Button variant="outline" onClick={() => setState("ready")}>Try Again</Button>
            </>
          )}

        </CardContent>
      </Card>
    </div>
  );
}
